/**
 * How a file is pointed at in a message: `@uploads/report.pdf`, the same
 * shape as a reference typed into Claude Code in a terminal. Paths with a
 * space are quoted so the reference still reads as one token.
 */
export function fileRef(path: string): string {
  if (/\s/.test(path)) return `@"${path}"`;
  // A bare `@report.html` would read as a mention, not a path; `./` makes
  // it unmistakably a file, as it would in a terminal.
  return path.includes('/') ? `@${path}` : `@./${path}`;
}

/** Matches what fileRef produces, for highlighting references in a sent prompt. */
export const FILE_REF_PATTERN = /@"[^"\n]+"|@[^\s"]+\/[^\s"]+/g;

export type FileKind = 'image' | 'pdf' | 'other';

const IMAGE = /\.(png|jpe?g|gif|webp|avif|bmp|svg|ico)$/i;

export function fileKind(name: string): FileKind {
  if (IMAGE.test(name)) return 'image';
  if (/\.pdf$/i.test(name)) return 'pdf';
  return 'other';
}

/** The file paths a message references, quotes and `@` stripped. */
export function referencedPaths(text: string): Set<string> {
  const paths = new Set<string>();
  for (const m of text.matchAll(FILE_REF_PATTERN)) {
    const path = m[0].startsWith('@"') ? m[0].slice(2, -1) : m[0].slice(1);
    paths.add(path.replace(/^\.\//, ''));
  }
  return paths;
}

/** `report.final.html` -> `html`; '' when there is none. */
export function extensionOf(path: string): string {
  const name = path.slice(path.lastIndexOf('/') + 1);
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(dot + 1).toLowerCase() : '';
}

/**
 * Snapshots are served as immutable for a year, and a browser keeps the
 * response headers with the body. Bump this when a header on them changes
 * (it went up to 2 when X-Frame-Options stopped being DENY), or browsers go
 * on using the old one. The server ignores it.
 */
const ARTIFACT_HEADERS_REV = 2;

/** The artifact endpoint for one version. `inline` shows it rather than saving it. */
export function artifactUrl(roomId: string, id: string, version: number, inline: boolean): string {
  const url = `/api/rooms/${encodeURIComponent(roomId)}/artifacts/${encodeURIComponent(id)}/v/${version}?r=${ARTIFACT_HEADERS_REV}`;
  return inline ? `${url}&inline` : url;
}

/** What Claude's publish_artifact result says it made. */
export function parsePublishResult(text: string): { id: string; version: number } | undefined {
  const m = /artifact ([0-9a-f-]{36}), version (\d+)/.exec(text);
  return m ? { id: m[1]!, version: Number(m[2]) } : undefined;
}
